import React, { useState, useEffect } from 'react';
import { history } from 'umi';
import { PagingQuery, TableColumn } from 'tea-component';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import FirstRowCustom from '@/pages/intelligence/components/TableComponents/components/FirstRowCustom';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import {
  SortByItemType,
} from '@/pages/intelligence/components/TableComponents/components/SortBy';
import {
  GenreHeader,
  GenreRender,
} from '@/pages/intelligence/components/TableComponents/components/TableGenre';
import AppMagicGenreTagList, { AppMagicGenreTagType } from '@/pages/intelligence/components/TableComponents/components/AppMagicGenreTagList';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import AppMagicIcon from '@/pages/intelligence/assets/imgs/dataSource/app-magic.png';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { handleSingleDate2RangeRangeTimeInFilterExtend } from '@intelligence/utils/dateUtils';
import { getSafeGenreSource, getGenreNameI18n } from '@intelligence/v3/utils/genre';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import { DetailsCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { StoreRankMoreTopColumn } from '@/pages/intelligence/_config/storeRank/columns';
import {
  analysisSourceMetricInfo,
  analysisPlatformTopInfo,
  getSourceMetric,
} from '@/pages/intelligence/_config/storeRank/utils';
import type {
  IBaseProps,
  IGameType,
  IGenreSource,
  ITranslater,
} from '@/pages/intelligence/types/common';
import type { TopchartTableProps } from '@/pages/intelligence/pages/_config/topchartsInterface';
import type {
  IStoreRankMoreCoreRankPrimaryKey,
  IStoreRankMoreCoreRankUnifiedKey,
  IMobileStoreRankSource,
  IPCStoreRankSource,
  IConsoleStoreRankSource,
  IStoreRankMoreCoreRank,
  IStoreRankMoreTableDimensionInfos,
} from '@/pages/intelligence/types/storeRank';
import type { IOptionWithChildren } from '@/pages/intelligence/components/GenreSelect/types';
import { WeiXinGameRender } from '@intelligence/pages/TopCharts/_config/TableCloums/WeiXinGameRender';
import {
  MINI_GAME_ENTITY_TYPE,
} from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';

const STORE_RANK_GENRE_SOURCES = ['annie', 'sensortower', 'iegg', 'appmagic', 'weixin'] as const;

const getNormalTitleContent = (
  columnMetadata: any,
  cfg: { entityType: IGameType; currentSortSourceMetric: string; formatMessage: ITranslater; },
) => {
  const { entityType, currentSortSourceMetric, formatMessage } = cfg || {};
  const titleContentLocaleKey = columnMetadata?.titleContent;
  if (!titleContentLocaleKey) return undefined;
  if (entityType === 'mobile' && currentSortSourceMetric === columnMetadata?.key)
    return (
      <>
        {formatMessage?.({ id: columnMetadata?.titleContent })}
        <br />
        {formatMessage?.(
          { id: 'dbi_store_rank_mobile_top_title_metric_tips' },
          { topName: formatMessage?.({ id: columnMetadata?.title }) as string },
        )}
      </>
    );
  if (columnMetadata?.titleInfo) {
    return (
      <>
        {formatMessage?.({ id: columnMetadata?.titleContent })}
        <br />
        {formatMessage?.({ id: columnMetadata?.titleInfo })}
      </>
    );
  }
  return formatMessage?.({ id: columnMetadata?.titleContent });
};

interface IStoreRankMoreCoreTablesProps extends IBaseProps, TopchartTableProps {
  entityType: IGameType;
  topChartsName: IMobileStoreRankSource | IPCStoreRankSource | IConsoleStoreRankSource;
  isSectional: boolean;
  isSelectable: boolean;
  isOperate: boolean;
  primaryKey: IStoreRankMoreCoreRankPrimaryKey;
  uunifiedKey: IStoreRankMoreCoreRankUnifiedKey;
  genreSource: IGenreSource;
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  sectionalRankKeys: string[];
  sectionalRankInfos: {
    [key: string]: IStoreRankMoreTableDimensionInfos;
  };
  platformTopMetrics: string[];
  platformTopInfos: {
    [key: string]: IStoreRankMoreTableDimensionInfos;
  };
  tableDimensionsData: string[];
  // 判断表格内 details按钮是否禁用、以及禁用时的提示文案
  getDetailsBtnDisabledInfo?: (cvm: IStoreRankMoreCoreRank) => {
    disabled?: boolean;
    disabledMsg?: string | React.ReactElement;
  };
  // App Magic 品类标签点击回调
  onAppMagicTagClick?: (type: AppMagicGenreTagType, superTag: string, majorTag?: string, midTag?: string, subTag?: string) => void;
}

const StoreRankMoreCoreTables: React.FC<IStoreRankMoreCoreTablesProps> = (props) => {
  const {
    entityType,
    topChartsName,
    isSectional,
    isSelectable,
    isOperate,
    primaryKey,
    uunifiedKey,
    loading,
    sectionalRankKeys = [],
    sectionalRankInfos = {},
    platformTopMetrics = [],
    platformTopInfos = {},
    tableDimensionsData,
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    genreSource = 'iegg',
    genreOptionsMap,
    getDetailsBtnDisabledInfo,
    onAppMagicTagClick,
    isHasDetailsColumns = true,
    isHasReleaseTimeColumns = true,
    isHasGenreColumns = true,
    gameColumnsWithCur,
  } = props;

  // console.debug('tableDimensionsData', props);

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  const { locale, formatMessage } = useIntlExtend(); // 中英切换方法
  const {
    tablePageIndex,
    tablePageSize,
    currentOrderSource,
    currentSortMetric,
    currentSortValue,
    currentRatioType,
  } = pageSortAttr || {};
  const currentSortSourceMetric = getSourceMetric({
    source: currentOrderSource,
    metric: currentSortMetric,
  });
  const currentSortKey = `${currentSortSourceMetric}_${currentRatioType}`;
  const defaultPageSize = 100;
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || defaultPageSize);
  const [selectList, setSelectList] = useState(tableSelection);

  const formatGenreName = (source: IGenreSource, genreValue?: string) => {
    if (!genreValue || !genreOptionsMap) {
      return genreValue || '';
    }

    const normalizedGenreSource = getSafeGenreSource(source, STORE_RANK_GENRE_SOURCES, 'iegg');

    return genreValue
      .split('|')
      .map((value) => {
        return getGenreNameI18n({
          locale,
          genreOptionsMap,
          genreSource: normalizedGenreSource,
          genreValue: value,
        });
      })
      .join('|');
  };
  const formatAppMagicGenreTag = (tag: string) => {
    return formatGenreName('appmagic', tag);
  };

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    onTablePagingChange(e);
  };

  const onSortChage = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getMetricColumns = (): TableColumn<IStoreRankMoreCoreRank>[] => {
    if (entityType === MINI_GAME_ENTITY_TYPE) {
      return (
        tableDimensionsData
          ?.filter((dimension: string) => platformTopMetrics?.includes(dimension))
          ?.sort((a, b) => {
            const aIdx = platformTopMetrics?.indexOf(a);
            const bIdx = platformTopMetrics?.indexOf(b);
            return aIdx - bIdx;
          })
          ?.map((dimension: string) => {
            const { source, metric, metricKey } = analysisSourceMetricInfo(dimension, entityType);
            // console.debug('analysisSourceMetricInfo', dimension, source, metric, metricKey)
            const { platform, top } = analysisPlatformTopInfo(source);
            // console.debug('analysisPlatformTopInfo', platform, top)
            const columnMetadata: any = {
              ...platformTopInfos?.[dimension],
              key: dimension,
              source,
              platform,
              top,
              metric,
              metricKey,
            };
            // console.debug('columnMetadata', columnMetadata)
            const titleContent = getNormalTitleContent(columnMetadata, {
              entityType: platform,
              currentSortSourceMetric,
              formatMessage,
            });
            // console.debug('titleContent', titleContent)
            return StoreRankMoreTopColumn({
              ...columnMetadata,
              locale,
              titleContent,
              currentSortKey,
              currentSortValue,
              onSortChage,
              entityType,
              width: 'auto',
              genreSource,
            });
          }) ?? []
      );
    }
    if (!isSectional) {
      return (
        tableDimensionsData
          ?.filter((dimension: string) => platformTopMetrics?.includes(dimension))
          ?.sort((a, b) => {
            const aIdx = platformTopMetrics?.indexOf(a);
            const bIdx = platformTopMetrics?.indexOf(b);
            return aIdx - bIdx;
          })
          ?.map((dimension: string) => {
            const { source, metric, metricKey } = analysisSourceMetricInfo(dimension, entityType);
            const { platform, top } = analysisPlatformTopInfo(source);
            const columnMetadata: any = {
              ...platformTopInfos?.[dimension],
              key: dimension,
              source,
              platform,
              top,
              metric,
              metricKey,
            };
            const titleContent = getNormalTitleContent(columnMetadata, {
              entityType,
              currentSortSourceMetric,
              formatMessage,
            });

            return StoreRankMoreTopColumn({
              ...columnMetadata,
              locale,
              titleContent,
              currentSortKey,
              currentSortValue,
              onSortChage,
              entityType,
            });
          }) ?? []
      );
    }
    return (
      tableDimensionsData
        ?.sort((a, b) => {
          const aIdx = sectionalRankKeys?.findIndex((item) => a?.endsWith(item));
          const bIdx = sectionalRankKeys?.findIndex((item) => b?.endsWith(item));
          return aIdx - bIdx;
        })
        ?.map((dimension) => {
          const { source, metric, metricKey } = analysisSourceMetricInfo(dimension, entityType);
          const { platform, top } = analysisPlatformTopInfo(source);
          const columnMetadata: any = {
            ...sectionalRankInfos?.[metric],
            key: dimension,
            source,
            platform,
            top,
            metric,
            metricKey,
          };
          const itemKey = columnMetadata?.key;
          const titleName = formatMessage({ id: columnMetadata?.title }) as string;
          const titleContent = (
            <>
              {formatMessage(
                { id: 'dbi_store_rank_sectional_title_metric_tips' },
                { rangeName: titleName },
              )}
              {currentSortSourceMetric === itemKey && (
                <>
                  <br />
                  {formatMessage({
                    id:
                      currentSortValue === 'desc'
                        ? 'dbi_store_rank_current_order_desc_sectional_title_metric_tips'
                        : 'dbi_store_rank_current_order_asc_sectional_title_metric_tips',
                  })}
                </>
              )}
            </>
          );
          return StoreRankMoreTopColumn({
            ...columnMetadata,
            titleContent,
            currentSortKey,
            currentSortValue,
            onSortChage,
            entityType,
          });
        }) ?? []
    );
  };

  const getGenreColumns = (): TableColumn<IStoreRankMoreCoreRank>[] => {
    const placeholder = '-';
    const appMagicWidth = 300;
    const superGenreWidth = 160;
    const normalizedGenreSource = getSafeGenreSource(genreSource, STORE_RANK_GENRE_SOURCES, 'iegg');

    // App Magic 品类：显示 Super Genre 和 Major/Mid/Sub Genre 两列（支持四级品类）
    if (normalizedGenreSource === 'appmagic') {
      /**
       * 构建二级、三级、四级品类的标签列表
       */
      const buildTagList = (majorCategoryStr: string, midCategoryStr: string, subCategoryStr: string) => {
        const majorTags = majorCategoryStr?.trim()?.split('|')?.filter(Boolean) || [];
        const midTags = midCategoryStr?.trim()?.split('|')?.filter(Boolean) || [];
        const subTags = subCategoryStr?.trim()?.split('|')?.filter(Boolean) || [];

        const tagList: { tag: string; type: 'major' | 'mid' | 'sub'; }[] = [];

        majorTags.forEach((tag) => {
          tagList.push({ tag, type: 'major' });
        });

        midTags.forEach((tag) => {
          tagList.push({ tag, type: 'mid' });
        });

        subTags.forEach((tag) => {
          tagList.push({ tag, type: 'sub' });
        });

        return tagList;
      };

      return [
        // App Magic Super Genre 列（一级品类）
        {
          key: 'super_genre',
          align: 'left',
          width: superGenreWidth,
          header: (
            <CloumsCenter width={superGenreWidth} align="left">
              <div className="general_table_genre-header">
                <img className="header-icon" src={AppMagicIcon} alt="" />
                <span>{formatMessage({ id: 'dbi_super_genre' })}</span>
              </div>
            </CloumsCenter>
          ),
          render: (cvm) => {
            const entityInfo = cvm?.entity_info;
            const data = entityInfo?.super_genre || cvm?.super_genre || '';
            const tags = data?.trim()?.split('|')?.filter(Boolean) || [];

            const handleTagClick = (tag: string) => {
              onAppMagicTagClick?.(AppMagicGenreTagType.SuperGenre, tag);
            };
            const content = tags.length > 0 && (
              <AppMagicGenreTagList
                tags={tags}
                tagFormatter={formatAppMagicGenreTag}
                onTagClick={handleTagClick}
              />
            );

            return (
              <CloumsCenter width={superGenreWidth} align="left">
                {content || placeholder}
              </CloumsCenter>
            );
          },
        },
        // App Magic Major/Mid/Sub Genre 列（二级/三级/四级品类）
        {
          key: 'major_category',
          align: 'left',
          width: appMagicWidth,
          header: (
            <CloumsCenter width={appMagicWidth} align="left">
              <div className="general_table_genre-header">
                <img className="header-icon" src={AppMagicIcon} alt="" />
                <span>{formatMessage({ id: 'dbi_top_appmagic_genre' })}</span>
              </div>
            </CloumsCenter>
          ),
          render: (cvm) => {
            const entityInfo = cvm?.entity_info;
            const majorCategoryStr = entityInfo?.major_category || cvm?.major_category || '';
            const midCategoryStr = entityInfo?.mid_category || cvm?.mid_category || '';
            const subCategoryStr = entityInfo?.sub_category || cvm?.sub_category || '';
            const tagList = buildTagList(majorCategoryStr, midCategoryStr, subCategoryStr);
            const tags = tagList.map((item) => item?.tag);

            const handleTagClickWithContext = (clickedTag: string) => {
              const superGenre = (entityInfo?.super_genre || cvm?.super_genre || '')?.split('|')?.filter(Boolean)?.[0] || '';
              const tagItem = tagList.find((item) => item?.tag === clickedTag);

              if (tagItem?.type === 'sub') {
                // 点击四级品类：传递一级、二级、三级、四级品类
                const majorGenre = majorCategoryStr?.split('|')?.filter(Boolean)?.[0] || '';
                const midGenre = midCategoryStr?.split('|')?.filter(Boolean)?.[0] || '';
                onAppMagicTagClick?.(AppMagicGenreTagType.SubGenre, superGenre, majorGenre, midGenre, clickedTag);
              } else if (tagItem?.type === 'mid') {
                // 点击三级品类：传递一级、二级、三级品类
                const majorGenre = majorCategoryStr?.split('|')?.filter(Boolean)?.[0] || '';
                onAppMagicTagClick?.(AppMagicGenreTagType.MidGenre, superGenre, majorGenre, clickedTag);
              } else {
                // 点击二级品类：传递一级、二级品类
                onAppMagicTagClick?.(AppMagicGenreTagType.MajorGenre, superGenre, clickedTag);
              }
            };
            const content = tags.length > 0 && (
              <AppMagicGenreTagList
                tags={tags}
                tagFormatter={formatAppMagicGenreTag}
                onTagClick={handleTagClickWithContext}
              />
            );

            return (
              <CloumsCenter width={appMagicWidth} align="left">
                {content || placeholder}
              </CloumsCenter>
            );
          },
        },
      ];
    }

    // // 普通品类列（data.ai / sensortower / iegg）- 都是2级品类（主品类+子品类）
    return [
      {
        key: 'genre',
        header: (
          <div className="header-other">
            <GenreHeader genreSource={normalizedGenreSource} />
          </div>
        ),
        width: normalizedGenreSource === 'weixin' ? '20%' : 220,
        align: 'left',
        render: (cvm) => {
          const genreInfo = getGenreInfo(normalizedGenreSource, cvm);
          return (
            <GenreRender
              mainGenre={formatGenreName(normalizedGenreSource, genreInfo?.genre) || placeholder}
              subGenre={formatGenreName(normalizedGenreSource, genreInfo?.sub_genre) || placeholder}
              genreSource={genreInfo?.genreSource || ''}
            />
          );
        },
      },
    ];
  };

  const getColumns = (): TableColumn<IStoreRankMoreCoreRank>[] => {
    const releaseTimeText = formatMessage({ id: 'dbi_release_date' }) as string;
    const columns: TableColumn<IStoreRankMoreCoreRank>[] = [
      {
        key: 'gametitle',
        header: (
          <div className="dbi-firstRow-flex">
            <div className="dbi-text-id">#</div>
            <div>{formatMessage({ id: 'dbi_top_gametitle' })}</div>
          </div>
        ),
        width: gameColumnsWithCur || 330,
        fixed: 'left',
        render: (cvm) => (<>{
          entityType === MINI_GAME_ENTITY_TYPE ? <FirstRowCustom
            id={cvm?.rank}
            cvm={cvm}
          /> : <FirstRow
            id={cvm?.rank}
            cvm={{
              gametitle: cvm?.name || '-',
              imgType: entityType,
              img: cvm?.cover,
              countryIcon: getCountryFlag(cvm?.publisher_addr),
              text: cvm?.desc || cvm?.publisher || '-',
            }}
            className={!getDetailGamePath(cvm) ? 'dbi-firstRow-flex--disabled' : ''}
            rihgtClickToJumpHref={getDetailGamePath(cvm)}
            onClick={() => {
              onClickGame(cvm);
            }}
          />}
        </>),
      },
      ...getMetricColumns(),
    ];
    if (isHasReleaseTimeColumns) {
      columns.push({
        key: 'release_time',
        header: releaseTimeText,
        align: 'left',
        width: 140,
        render: (cvm: any) => {
          const formattedReleaseTime = formatReleaseTime(locale, {
            isSpecialReleaseTime: cvm?.is_special_release_time,
            releaseTime: cvm?.release_time,
            releaseTimeEN: cvm?.release_time_en,
            releaseTimeCN: cvm?.release_time_cn,
          });
          // 仅当严格匹配 YYYY-MM-DD HH:mm:ss 时截为 YYYY-MM-DD，避免 2020、2020-02 等被 moment 误解析
          const isStandardDateTime = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(formattedReleaseTime);
          const releaseTime = (isStandardDateTime ? formatDateStr(formattedReleaseTime) : formattedReleaseTime) || '-';
          return (
            <CloumsCenter width={140} align="left">
              {releaseTime}
            </CloumsCenter>
          );
        },
      });
    }
    if (isHasGenreColumns) {
      columns.push(...getGenreColumns());
    }
    if (isHasDetailsColumns) {
      columns.push(
        DetailsCloums({
          getDisabledInfo: (cvm: IStoreRankMoreCoreRank) => {
            const disabledInfo = getDetailsBtnDisabledInfo?.(cvm);
            return {
              ...disabledInfo,
              disabled: disabledInfo?.disabled || !getDetailGamePath(cvm),
            };
          },
          onClickGame: (cvm: any) => {
            onClickGame(cvm);
          },
          formatMessage,
        }) as TableColumn<IStoreRankMoreCoreRank>,
      );
    }
    if (isSelectable) {
      columns.unshift({
        key: 'select',
        width: 36,
        fixed: 'left',
        header: () => (
          <TableCheckboxHeader
            selectList={selectList}
            tableSelectionAll={tableSelectionAll}
            disabled={loading || records.length === 0}
            onChange={(list, stats) => {
              onTableCheckGame(list, stats);
              setSelectList(list);
            }}
          />
        ),
        render: (cvm) => (
          <TableCheckboxRender
            selectList={selectList}
            cvm={{ ...(cvm || {}), gameId: cvm?.[primaryKey] } as any}
            onChange={(list, status) => {
              setSelectList(list);
              onTableCheckGame(list, status);
            }}
          />
        ),
      });
    }
    // console.debug('columnscolumnscolumns', columns)
    return columns;
  };

  const getGenreInfo = (
    genreSource: IGenreSource,
    cvm: IStoreRankMoreCoreRank,
  ): { genre: string; sub_genre: string; } => {
    const fieldMapping: Record<string, { genre: string; sub_genre: string; }> = {
      iegg: { genre: 'iegg_genre', sub_genre: 'iegg_sub_genre' },
      sensortower: { genre: 'sensortower_genre', sub_genre: 'sensortower_sub_genre' },
      annie: { genre: 'genre', sub_genre: 'sub_genre' },
      weixin: { genre: 'minigame_genre', sub_genre: 'minigame_sub_genre' },
    };

    const fields = fieldMapping[genreSource];
    if (!fields) {
      return { genre: '-', sub_genre: '-' };
    }

    return {
      genre: (cvm as any)?.[fields.genre] || '-',
      sub_genre: (cvm as any)?.[fields.sub_genre] || '-',
      genreSource,
    } as any;
  };

  const getDetailGamePath = (cvm: IStoreRankMoreCoreRank) => {
    /** PC / Console 有 combine_id 时跳聚合详情，缺失时才回退原单端详情。 */
    const shouldUseCombineDetail =
      (entityType === 'pc' || entityType === 'console') && !!cvm?.combine_id;
    const gameId = shouldUseCombineDetail ? cvm.combine_id : cvm?.[uunifiedKey];
    // 无实体行没有详情 ID，空路径会关闭右键跳转，左键在 onClickGame 中直接返回。
    if (!gameId) return '';

    const searchParams = new URLSearchParams();
    searchParams.set('source', encodeURIComponent(topChartsName));
    const path = getDetailGamePathCommon({
      entityType: shouldUseCombineDetail ? 'combine' : entityType,
      gameId,
      tab: 'storeRank',
      subTab: '',
    });
    return `${path}?${searchParams?.toString()}`;
  };

  const onClickGame = (cvm: IStoreRankMoreCoreRank) => {
    const detailPath = getDetailGamePath(cvm);
    if (!detailPath) return;

    if (isOperate) {
      const filterExtendTmp = handleSingleDate2RangeRangeTimeInFilterExtend(filterExtend);
      setFilterExtend(filterExtendTmp);
    }
    sessionStorage.setItem('isFromRank', 'true');
    history.push(detailPath);
  };

  return (
    <>
      {/* 如果没有排序 header-other 用个类名 有排序用这个 table-header */}

      <TopchartTable
        recordKey={primaryKey}
        loading={loading || false}
        records={records}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={getColumns()}
      />
    </>
  );
};

export default StoreRankMoreCoreTables;
